import { useAuthStore } from '@features/auth/store/authStore';
import { useCallback, useState } from 'react';
import { MediaService } from '../services/media.service';
import {
  MediaFile,
  MediaUploadOptions,
  MediaUploadProgress,
} from '../types/media.types';

export interface UseMediaUploadOptions extends MediaUploadOptions {
  testId?: string;
}

export const useMediaUpload = (options: UseMediaUploadOptions = {}) => {
  const [uploadProgress, setUploadProgress] = useState<
    Map<string, MediaUploadProgress>
  >(new Map());
  const { user } = useAuthStore();

  const handleProgress = useCallback((progress: MediaUploadProgress) => {
    setUploadProgress((prev) => new Map(prev).set(progress.id, progress));
  }, []);

  const uploadMedia = useCallback(
    async (file: MediaFile): Promise<string> => {
      if (!user?.uid) {
        throw new Error('User not authenticated');
      }

      try {
        // Extract testId from options
        const { testId, ...uploadOptions } = options;

        return await MediaService.uploadMedia(
          user.uid,
          file,
          uploadOptions,
          handleProgress,
          testId
        );
      } catch (error) {
        console.error('Error uploading media:', error);
        throw error;
      }
    },
    [user, options, handleProgress]
  );

  const uploadMultiple = useCallback(
    async (files: MediaFile[]): Promise<string[]> => {
      return Promise.all(files.map((file) => uploadMedia(file)));
    },
    [uploadMedia]
  );

  const getProgress = useCallback(
    (fileId: string): MediaUploadProgress | undefined => {
      return uploadProgress.get(fileId);
    },
    [uploadProgress]
  );

  return {
    uploadMedia,
    uploadMultiple,
    uploadProgress: uploadProgress,
    getProgress,
  };
};
